import { useEffect, useRef } from "react";

export const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));

// Fenêtre d'apparition : renvoie 0 avant "from", 1 après "to", et le dégradé entre les deux.
export const range = (p, from, to) => clamp((p - from) / (to - from));

/**
 * Lie la lecture d'une vidéo à la position de scroll d'une section.
 *
 * Le scroll écrit seulement une valeur cible ; une boucle d'animation la lisse
 * puis déplace la tête de lecture. Écrire currentTime directement dans
 * l'écouteur de scroll fait saccader — ne pas le faire.
 *
 * @param {(p:number)=>void} onProgress appelé à chaque image avec la position lissée (0 → 1)
 * @param {number} ease inertie : plus bas = plus doux (0.12 par défaut)
 */
export function useScrollVideo(onProgress, ease = 0.12) {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const targetRef = useRef(0);
  const currentRef = useRef(0);
  const cbRef = useRef(onProgress);
  const activeRef = useRef(true);
  const primedRef = useRef(false);
  cbRef.current = onProgress;

  useEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    if (!section || !video) return undefined;

    video.muted = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");

    const seekToTarget = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      const nextTime = targetRef.current * Math.max(0, video.duration - 0.05);
      if (Math.abs(video.currentTime - nextTime) <= 0.01) return;
      try {
        video.currentTime = nextTime;
      } catch {
        // Safari peut refuser un seek tant que le lecteur natif n'est pas prêt.
      }
    };

    const prime = () => {
      if (primedRef.current) return;
      primedRef.current = true;
      if (video.readyState === 0) video.load();
      const attempt = video.play();
      if (attempt?.then) {
        attempt
          .then(() => {
            video.pause();
            seekToTarget();
            document.removeEventListener("touchstart", prime);
            document.removeEventListener("pointerdown", prime);
          })
          .catch(() => {
            primedRef.current = false;
          });
      } else {
        video.pause();
        seekToTarget();
      }
    };

    const onScroll = () => {
      const stage = section.querySelector("[data-scroll-video-stage]");
      const viewportHeight = stage?.clientHeight || window.visualViewport?.height || window.innerHeight;
      const runway = section.offsetHeight - viewportHeight;
      targetRef.current = runway > 0 ? clamp(-section.getBoundingClientRect().top / runway) : 0;
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        activeRef.current = entry.isIntersecting;
        if (entry.isIntersecting) prime();
      },
      { rootMargin: "80% 0px" },
    );

    onScroll();
    observer.observe(section);
    video.addEventListener("loadedmetadata", seekToTarget);
    document.addEventListener("touchstart", prime, { passive: true });
    document.addEventListener("pointerdown", prime, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    window.visualViewport?.addEventListener("resize", onScroll);
    return () => {
      observer.disconnect();
      video.removeEventListener("loadedmetadata", seekToTarget);
      document.removeEventListener("touchstart", prime);
      document.removeEventListener("pointerdown", prime);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.visualViewport?.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(() => {
    let id;
    const tick = () => {
      const diff = targetRef.current - currentRef.current;
      currentRef.current += diff * ease;
      if (Math.abs(diff) < 0.0002) currentRef.current = targetRef.current;
      const p = currentRef.current;

      const v = videoRef.current;
      if (activeRef.current && v && Number.isFinite(v.duration) && v.duration > 0) {
        const t = p * (v.duration - 0.05);
        if (Math.abs(v.currentTime - t) > 0.01) {
          try {
            v.currentTime = t;
          } catch {
            // Le prochain frame réessaiera après initialisation du lecteur.
          }
        }
      }
      cbRef.current?.(p);
      id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [ease]);

  return { sectionRef, videoRef };
}
